import type { ConnectionPhase } from '@parsleyai/ui'
import { useContext, useMemo } from 'react'
import { MealLogContext } from '../mealLog/MealLogContext'
import { deriveCookActivity } from './activity'
import { MealLibraryContext } from './MealLibraryContext'
import { toLibraryMeal } from './toLibraryMeal'
import type { LibraryMeal } from './types'

export interface UseMealLibraryResult {
  // Every library meal as a render-ready view-model, newest first. Hidden meals are
  // FLAGGED (`.hidden`), not dropped — consumers filter (the grid excludes them; a
  // "removed" undo needs them present). Excludes nothing else.
  meals: LibraryMeal[]
  loading: boolean
  error: string | null
  phase: ConnectionPhase
  retry: () => void
}

// The library read layer. Joins the two live subscriptions (meals + user state)
// with per-meal cook activity derived from the cook log, into LibraryMeal[]. Pages consume ONLY this — never the Firestore shapes, the meals
// store, or the plan docs directly (mirrors usePlans/usePlan).
export function useMealLibrary(): UseMealLibraryResult {
  const ctx = useContext(MealLibraryContext)
  if (!ctx) throw new Error('useMealLibrary must be used within MealLibraryProvider')
  const { state, phase, retry } = ctx
  const log = useContext(MealLogContext)
  if (!log) throw new Error('useMealLibrary must be used within MealLogProvider')

  const activity = useMemo(() => deriveCookActivity(log.entries), [log.entries])

  const meals = useMemo(() => {
    return state.meals
      .map((m) => toLibraryMeal(m, state.userState[m.mealId], activity.get(m.mealId)))
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
  }, [state.meals, state.userState, activity])

  return { meals, loading: state.loading, error: state.error, phase, retry }
}
